import React from 'react';
import { motion } from 'framer-motion';
import LeadsTableRow from './LeadsTableRow/LeadsTableRow';
import LeadsMobileCard from './LeadsMobileCard/LeadsMobileCard';
import { SavedLead } from '../../../../services/leadsService';
import { useTranslation } from 'react-i18next';
import CustomCheckbox from '../../../../components/CustomCheckbox/CustomCheckbox';

interface Lead extends SavedLead { }

interface LeadsTableProps {
  leads: Lead[];
  selectedLeads: string[];
  setSelectedLeads: (ids: string[]) => void;
  onDeleteLead: (id: string) => void;
  onLeadUpdated: (updatedLead: SavedLead) => void;
  'data-tour'?: string;  // Suporte para tour
}

const LeadsTable: React.FC<LeadsTableProps> = ({
  leads,
  selectedLeads,
  setSelectedLeads,
  onDeleteLead,
  onLeadUpdated,
  'data-tour': dataTour,  // Extrai data-tour
}) => {
  const { t } = useTranslation();

  const handleRowSelect = (id: string) => {
    if (selectedLeads.includes(id)) {
      setSelectedLeads(selectedLeads.filter(leadId => leadId !== id));
    } else {
      setSelectedLeads([...selectedLeads, id]);
    }
  };

  // Lógica para o checkbox "Marcar/Desmarcar Todos" na página atual
  const allDisplayedSelected = leads.length > 0 && leads.every(r => selectedLeads.includes(r.id));

  const handleSelectAll = () => {
    if (allDisplayedSelected) {
      // Desmarcar todos na página atual
      setSelectedLeads(selectedLeads.filter(id => !leads.map(r => r.id).includes(id)));
    } else {
      // Marcar todos na página atual
      const newSelectedIds = leads.map(r => r.id).filter(id => !selectedLeads.includes(id));
      setSelectedLeads([...selectedLeads, ...newSelectedIds]);
    }
  };


  return (
    <motion.div
      className="rounded-xl bg-white dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50 overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 0.5 }}
    >
      <div className="md:hidden p-4 bg-gray-50 dark:bg-gray-800/20">
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="flex items-center gap-2">
            <CustomCheckbox
              checked={allDisplayedSelected}
              onChange={handleSelectAll}
            />
            <span className="text-sm text-gray-500 dark:text-gray-400">{t('Selecionar todos')}</span>
          </div>
        </div>
        {leads.map((lead) => (
          <LeadsMobileCard
            key={lead.id}
            lead={lead}
            isSelected={selectedLeads.includes(lead.id)}
            onSelect={() => handleRowSelect(lead.id)}
            onDelete={onDeleteLead}
            onLeadUpdated={onLeadUpdated}
          />
        ))}
      </div>

      <div
        className="overflow-x-auto hidden md:block"
        data-tour={dataTour}  // Aplica data-tour para o tour
      >
        <table className="w-full text-left">
          <thead className="border-b border-gray-200 dark:border-primary-darkMode bg-gray-50 dark:bg-background-dark-elevated">
            <tr>
              <th className="p-4 w-8">
                <CustomCheckbox
                  checked={allDisplayedSelected}
                  onChange={handleSelectAll}
                />
              </th>
              <th className="p-4 text-sm font-semibold text-gray-500 dark:text-white uppercase tracking-wider">{t('leads.table_header_prospect')}</th>
              <th className="p-4 text-sm font-semibold text-gray-500 dark:text-white uppercase tracking-wider">{t('leads.table_header_phone')}</th>
              <th className="p-4 text-sm font-semibold text-gray-500 dark:text-white uppercase tracking-wider">{t('leads.table_header_country')}</th>
              <th className="p-4 text-sm font-semibold text-gray-500 dark:text-white uppercase tracking-wider text-center">{t('leads.table_header_whatsapp')}</th>
              <th className="p-4 text-sm font-semibold text-gray-500 dark:text-white uppercase tracking-wider text-center">{t('leads.table_header_prospected')}</th>
              <th className="p-4 text-sm font-semibold text-gray-500 dark:text-white uppercase tracking-wider text-center">{t('leads.table_header_rating')}</th>
              <th className="p-4 text-sm font-semibold text-gray-500 dark:text-white uppercase tracking-wider">{t('leads.table_header_actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {leads.map((lead, index) => (
              <LeadsTableRow
                key={lead.id}
                lead={lead}
                isSelected={selectedLeads.includes(lead.id)}
                onSelect={() => handleRowSelect(lead.id)}
                onDelete={onDeleteLead}
                onLeadUpdated={onLeadUpdated}
                index={index}
              />
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

export default LeadsTable;